import * as CryptoTS from 'crypto-ts';
import { useRoutes } from 'react-router-dom';
import routes, { prefetchRouteChunks } from './router';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ThemeProvider from './theme/ThemeProvider';
import { CssBaseline } from '@mui/material';
import { useEffect, useState } from 'react';
import { Keywordcontext } from './context/keywordcontext';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { setGlobalState, useGlobalState } from './state/globalstate';
import IdleTimer from './session/idle';
import { version } from 'react';
import Axios, { API } from './common/api';
import * as crypto from 'crypto';
import React from 'react';
import { identifyFromStorage } from './common/analytics';
import { checkConnectionNow, subscribeConnection } from './common/connection';
import { initFrontendLog, logInfo } from './common/frontendLog';
import { fetchRoleLimits } from './common/roleLimits';
import { eDiscoveryUrl } from './common/routes';
// react-chat-widget removed from the initial bundle — it was imported but only
// used in commented-out code, so it was dead weight on every page load.
import ArkChatbot from './components/ArkChatbot';

// import logo from './logo.svg';

declare global {
  interface Window {
    analytics: any;
  }
}

const App = () => {
  // // //console.log(version);
  const [keyword, setKeyword] = useState<string>('');
  const value = { keyword, setKeyword };

  // What the connectivity bar is saying. null = hidden.
  //   'offline' — no network link on this machine; the fix is at the user's end.
  //   'server'  — we have a link but the API isn't usable: nothing answering, or
  //               answering 5xx while the services power on. Same sentence for
  //               both, because the user's situation and options are identical.
  //   'online'  — just recovered; green, then hides itself.
  // Anything that is NOT one of these (4xx, a plain 500, a render fault) is a real
  // error and still goes to the error page — the bar is only for "wait it out".
  const [networkStatus, setNetworkStatus] = useState<
    'offline' | 'server' | 'online' | null
  >(null);
  const content = useRoutes(routes);

  // Re-identify a signed-in user to PostHog whenever the app re-opens, so
  // returning sessions are attributed without waiting for a fresh login.
  // Also load the Demo-role feature limits for returning demo sessions.
  useEffect(() => {
    // Start the durable activity log first: it installs the global error hooks
    // and, more importantly, drains anything buffered by a PREVIOUS load — which
    // is how an outage that killed every request still ends up in the logs.
    initFrontendLog();
    identifyFromStorage();
    fetchRoleLimits();
    // Warm all lazy route chunks in the background after first paint so opening
    // each screen mounts its own skeleton instantly instead of flashing the
    // generic "Loading your workspace" route loader.
    prefetchRouteChunks();
  }, []);

  const [modalmessage] = useGlobalState('modalmessage');

  if (modalmessage) {
    modalmessage;
    setGlobalState('modalmessage', null);
  }

  const [loggedin] = useGlobalState('loggedin');

  //Remove console messages
  if (process.env.NODE_ENV === 'production') {
    console.log = () => {};
    console.error = () => {};
    console.debug = () => {};
    console.warn = () => {};
    console.info = () => {};
    console.group = () => {};
    console.groupCollapsed = () => {};
    console.trace = () => {};
  } else if (process.env.NODE_ENV === 'development') {
    console.info = () => {};
    console.group = () => {};
    console.warn = () => {};
    console.groupCollapsed = () => {};
    console.trace = () => {};
  }

  // Reserve a dedicated strip at the bottom for the connectivity bar so it never
  // overlaps the footer or the floating chatbot. We push page content up with
  // body padding and lift the chatbot's launcher and panel above the 44px bar
  // whenever the bar is visible.
  useEffect(() => {
    const STYLE_ID = 'network-bar-spacing';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.innerHTML = `
        body.network-bar-open { padding-bottom: 68px; }
        body.network-bar-open .ark-chatbot__launcher,
        body.network-bar-open .ark-chatbot__panel {
          bottom: 68px;
          max-height: calc(100vh - 92px);
        }
        @media (max-width: 520px) {
          body.network-bar-open .ark-chatbot__panel {
            bottom: 44px;
            height: calc(100% - 44px);
            max-height: none;
          }
        }
        @keyframes network-bar-shine {
          0% { transform: translateX(-120%) skewX(-20deg); }
          100% { transform: translateX(320%) skewX(-20deg); }
        }
      `;
      document.head.appendChild(style);
    }

    if (networkStatus) {
      document.body.classList.add('network-bar-open');
    } else {
      document.body.classList.remove('network-bar-open');
    }

    return () => {
      document.body.classList.remove('network-bar-open');
    };
  }, [networkStatus]);

  // Internet connectivity notice: a sticky bottom bar shows while the app can't
  // reach the API, and switches to a "back online" state that hides itself once
  // it can again.
  //
  // Driven by common/connection, i.e. by whether requests actually get through
  // — NOT by `navigator.onLine`, which stays true through the failures people
  // hit in practice (a laptop waking with dead sockets, a VPN drop, the API
  // being unreachable). Those left the user with a silent spinner and no clue
  // that a refresh was needed; now the bar names the problem and offers the fix.
  useEffect(
    () =>
      subscribeConnection((state) => {
        const next =
          state === 'offline'
            ? 'offline'
            : state === 'lost' || state === 'unavailable'
            ? 'server'
            : state === 'restored'
            ? 'online'
            : null;
        // Record what the user was actually shown, not just what the app knew.
        // connection.ts logs the state change; this says the notice reached the
        // screen, which is the difference between "we detected it" and "they saw
        // it" when someone reports having had no idea anything was wrong.
        if (next) {
          logInfo('connection_bar_shown', {
            msg: `Showed the "${next}" connectivity bar`,
            extra: { bar: next }
          });
        }
        setNetworkStatus(next);
      }),
    []
  );

  // Cross-tab session sync (localStorage is shared across tabs):
  //  - Signed OUT in another tab  → mirror the logout and go to /login.
  //  - Signed IN in another tab    → bounce any stale auth tab (e.g. a /login
  //    tab left open) straight to the dashboard, so it can't sit idle and later
  //    clear the shared session out from under an active work tab.
  useEffect(() => {
    const onStorage = () => {
      const path = window.location.pathname.toLowerCase();
      const onAuthPage =
        path.includes('login') ||
        path.includes('register') ||
        path.includes('forget') ||
        path.includes('awsauth') ||
        path.includes('demo-');
      const hasToken = !!localStorage.getItem('token');
      if (!hasToken) {
        setGlobalState('loggedin', false);
        if (!onAuthPage) {
          window.location.href = '/login';
        }
      } else if (onAuthPage) {
        setGlobalState('loggedin', true);
        window.location.href = eDiscoveryUrl.CaseS3 + '/1';
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  //Get S3 Path
  useEffect(() => {
    let Count = 0;
    const getFilePath = async () => {
      try {
        await Axios.get(API.S3UploadPath).then((res) => {
          // store the data into our books variable
          setGlobalState('BucketType', res.data.s3BucketName.toString());
          setGlobalState('BucketPath', res.data.BucketPath.toString());
          var bytes = CryptoTS.AES.decrypt(
            res.data.accessKeyId,
            process.env.REACT_APP_DECRYPTION_KEY || 'ARC TAH SHA'
          );
          var accessKeyId = bytes.toString(CryptoTS.enc.Utf8);
          setGlobalState('accessKeyId', accessKeyId);
          var bytes = CryptoTS.AES.decrypt(
            res.data.secretAccessKey,
            process.env.REACT_APP_DECRYPTION_KEY || 'ARC TAH SHA'
          );
          var secretAccessKey = bytes.toString(CryptoTS.enc.Utf8);
          console.log('Keys ====');
          console.log(res.data.BucketRegion, secretAccessKey, accessKeyId);
          setGlobalState('secretAccessKey', secretAccessKey);
          setGlobalState('BucketRegion', res.data.BucketRegion);
          // Consolidated creds for client-side Textract OCR of drawn image regions.
          setGlobalState('awsCreds', {
            accessKeyId,
            secretAccessKey,
            region: res.data.BucketRegion
          });
          setGlobalState('ErrorFoundGlobal', true);
          // console.log('kEYS');
          // console.log(
          //   res.data.s3BucketName.toString(),
          //   res.data.BucketPath.toString(),
          //   accessKeyId,
          //   secretAccessKey
          // );
        });
      } catch (err) {
        setGlobalState('ErrorFoundGlobal', false);
        // getFilePath();
      }
    };
    getFilePath();

    // Get language parameters
    const Language = async () => {
      let selectedLanguage = localStorage.getItem('language');
      let jsonFile = 2;
      if (!selectedLanguage) {
        jsonFile = 2;
        selectedLanguage = 'US - Commercial';
        localStorage.setItem('languageType', '2');
      }

      if (selectedLanguage == '中国 - 商业') {
        jsonFile = 1;
        localStorage.setItem('languageType', '1');
      } else if (selectedLanguage == 'US - Commercial') {
        jsonFile = 2;
        localStorage.setItem('languageType', '2');
      }

      const params = { commercialId: jsonFile };
      //Language listing
      try {
        await Axios.get(API.getCommercials, {
          params
        }).then(async (res) => {
          const languageResponse = res.data;
          // console.log(languageResponse);
          setGlobalState('languageArray', languageResponse);
          localStorage.setItem('language', selectedLanguage);
          return Promise.resolve();
        });
      } catch (error) {
        // const ERROR_MSG = 'Unable to proceed with your request';
        // setGlobalState('modalmessage', toast.error(ERROR_MSG));
        return Promise.reject();
      }
    };
    Language();
  }, []);

  // useEffect(() => {
  //   addResponseMessage('Welcome to this Regtool Q chat!');
  // }, []);

  // const handleNewUserMessage = async (newMessage) => {
  //   let chatResponse = '';
  //   toggleMsgLoader();
  //   //console.log(`New message incoming! ${newMessage}`);
  //   const params = { question: encodeURIComponent(newMessage) };
  //   try {
  //     await Axios.get(API.mlrcBedrock, {
  //       params
  //     }).then((res) => {
  //       // store the data into our books variable
  //       //console.log(res.data.response);
  //       chatResponse = res.data.response;
  //       // let chatResponse = res.data;
  //     });
  //   } catch (err) {
  //     // setGlobalState('ErrorFoundGlobal', false);
  //     // //console.log(err);
  //   }

  //   addResponseMessage(chatResponse);
  //   toggleMsgLoader();
  //   // Now send the message throught the backend API
  // };

  // Replaces the Voiceflow widget and, like it, shows on every page. Keyed by
  // user so logging in/out remounts it: the previous user's conversation must
  // never stay on screen for the next person at the login page.
  const chatbotUserKey = localStorage.getItem('userId') ?? 'anon';

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />

        <IdleTimer />
        <Keywordcontext.Provider value={value}>
          {content}
        </Keywordcontext.Provider>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <ArkChatbot key={chatbotUserKey} />
        {networkStatus && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2147483647,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '0 16px',
              boxSizing: 'border-box',
              overflow: 'hidden',
              color: '#fff',
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: 0.2,
              background:
                networkStatus === 'online'
                  ? 'linear-gradient(180deg, #16a34a 0%, #15803d 100%)'
                  : 'linear-gradient(180deg, #dc2626 0%, #b91c1c 100%)',
              borderTop: '1px solid rgba(255, 255, 255, 0.22)',
              boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.18)'
            }}
          >
            {/* light sweep across the bar */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '35%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
                animation: 'network-bar-shine 2.8s ease-in-out infinite',
                pointerEvents: 'none'
              }}
            />
            {/* animated AI orb: SVG (SMIL) counter-rotating arcs around a pulsing core */}
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              style={{ flexShrink: 0, display: 'block' }}
            >
              {/* faint track */}
              <circle
                cx="12"
                cy="12"
                r="9"
                fill="none"
                stroke="rgba(255,255,255,0.28)"
                strokeWidth="2.5"
              />
              {/* outer arc, clockwise */}
              <path
                d="M12 3 a9 9 0 0 1 8.5 6"
                fill="none"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  attributeType="XML"
                  type="rotate"
                  from="0 12 12"
                  to="360 12 12"
                  dur="0.9s"
                  repeatCount="indefinite"
                />
              </path>
              {/* inner arc, counter-clockwise */}
              <path
                d="M12 7 a5 5 0 0 0 -4.7 3.3"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  attributeType="XML"
                  type="rotate"
                  from="360 12 12"
                  to="0 12 12"
                  dur="0.7s"
                  repeatCount="indefinite"
                />
              </path>
              {/* pulsing core */}
              <circle cx="12" cy="12" r="2" fill="#fff">
                <animate
                  attributeName="r"
                  values="1.2;2.4;1.2"
                  dur="1.2s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.6;1;0.6"
                  dur="1.2s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <span style={{ position: 'relative' }}>
              {networkStatus === 'offline'
                ? // The machine has no link, so name that: telling someone the
                  // server is unreachable sends them looking in the wrong place.
                  "You're offline. Check your internet connection — we'll reconnect automatically."
                : networkStatus === 'server'
                ? "Can't reach the server. Anything in progress is paused — we'll reconnect automatically."
                : // No "Reload the page" instruction: the green bar carries no
                  // button any more, so telling the user to reload dangles. Say
                  // what happened and that they can carry on.
                  "Back online. You're reconnected — carry on where you left off."}
            </span>
            {/* Only for the server case, and only because it's the one state where
                pressing something can actually change the answer — a re-probe may
                find the services back up. Offline has nothing to re-check (there
                is no link; the browser raises `online` by itself the moment there
                is), and the green bar is already telling the user the good news
                and hiding itself, so a button on either is a control that does
                nothing useful. */}
            {networkStatus === 'server' && (
              <button
                type="button"
                onClick={() => checkConnectionNow()}
                style={{
                  position: 'relative',
                  flexShrink: 0,
                  border: '1px solid rgba(255,255,255,0.55)',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.14)',
                  color: '#fff',
                  font: 'inherit',
                  fontSize: 12.5,
                  padding: '4px 14px',
                  cursor: 'pointer'
                }}
              >
                Try now
              </button>
            )}
          </div>
        )}
        {/* <div className="App">
          <Widget
            handleNewUserMessage={handleNewUserMessage}
            // profileAvatar={logo}
            title="Regtool Q"
            // subtitle="And my cool subtitle"
          />
        </div> */}
      </LocalizationProvider>
    </ThemeProvider>
  );
};
export default App;
